import type { ListBuilderColumn } from '../../types';

/**
 * Shared column builders for the purpose-built compliance lists (PD-16252).
 * Every list has the same five-part row — Account | License | one list-specific
 * column | Why it's here | action — so the columns carry comparable weight
 * instead of one wide column beside several near-empty ones.
 */
const TABLE = 'client_license';
const plain = { size: 'sm' as const, colorRule: { type: 'default' as const } };
const muted = {
	size: 'xs' as const,
	colorRule: { type: 'theme' as const, themeColor: 'textSecondary' as const },
};
const lineBreak = (id: string): ListBuilderColumn['fields'][number] => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	displayStyle: 'line_break',
});

/** Account: name (links to the client), city/state, and 90-day sales. */
export const accountColumn: ListBuilderColumn = {
	id: 'compliance-account',
	fields: [
		{
			id: 'account-name',
			link: {
				type: 'custom',
				enabled: true,
				urlTemplate: '/clients/{clientId}',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('account-break-1'),
		{
			id: 'account-location',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientCityStateZip', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Account',
	headers: [{ id: 'h-account', text: 'Account' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'clientName', objectTable: TABLE },
	],
	minWidth: 190,
};

/** 90-day gross sales: the size of the account, and so of the risk. */
export const salesColumn: ListBuilderColumn = {
	id: 'compliance-sales-90',
	fields: [
		{
			id: 'sales-90-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientRevenue90', objectTable: TABLE },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'currency', decimalPlaces: 0 },
			},
			displayStyle: 'currency',
		},
	],
	label: 'Sales 90d',
	headers: [{ id: 'h-sales-90', text: 'Sales 90d' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: true,
	sortFields: [
		{ direction: 'DESC', fieldName: 'clientRevenue90', objectTable: TABLE },
	],
	width: 100,
};

/** License: number (links to the license) over its type. */
export const licenseColumn: ListBuilderColumn = {
	id: 'compliance-license',
	fields: [
		{
			id: 'license-number',
			link: { type: 'custom', enabled: true, urlTemplate: '/licenses/{id}' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseNumber', objectTable: TABLE },
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		lineBreak('license-break-1'),
		{
			id: 'license-type',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'typeLabel', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'License',
	headers: [{ id: 'h-license', text: 'License' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'licenseNumber', objectTable: TABLE },
	],
	width: 150,
};

/** Your file vs the regulator: "Active to 3/31/28" over "Cancelled 9/8/25". */
export const fileVsRegulatorColumn: ListBuilderColumn = {
	id: 'compliance-file-vs-regulator',
	fields: [
		{
			id: 'regulator-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'regulatorSummary', objectTable: TABLE },
				defaultValue: 'No record',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				// Colored by the regulator's status, not always red: an in-force
				// license on the Expiring Soon or Link Ended list is not an alarm.
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'regulatorStatus', objectTable: TABLE },
							operator: 'equals',
							value: 'Active',
							color: { themeColor: 'statusSuccess' },
						},
						{
							field: { fieldName: 'regulatorStatus', objectTable: TABLE },
							operator: 'in',
							value: ['Pending', 'Probation', 'Limited'],
							color: { themeColor: 'statusWarning' },
						},
					],
					defaultColor: { themeColor: 'statusError' },
				},
			},
			displayStyle: 'text',
		},
		lineBreak('regulator-break-1'),
		{
			id: 'file-summary-label',
			dataConfig: { type: 'custom_text', customText: 'Your file:' },
			styleConfig: muted,
			displayStyle: 'text',
		},
		{
			id: 'file-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fileSummary', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Regulator vs Your File',
	headers: [{ id: 'h-file-vs-regulator', text: 'Regulator vs Your File' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 180,
};

/** Wrong Number: who the regulator says holds the number on file. */
export const numberBelongsToColumn: ListBuilderColumn = {
	id: 'compliance-number-belongs-to',
	fields: [
		{
			id: 'regulator-holder',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'regulatorHolder', objectTable: TABLE },
				defaultValue: 'Unknown holder',
			},
			styleConfig: { ...plain, weight: 'medium', allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Number Belongs To',
	headers: [{ id: 'h-number-belongs-to', text: 'Number Belongs To' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 210,
};

/** Controlled, No DEA: what the account bought ("C2, C4 · last 9/4/2026 · 14 invoices"). */
export const controlledPurchasesColumn: ListBuilderColumn = {
	id: 'compliance-controlled-purchases',
	fields: [
		{
			id: 'controlled-purchases',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'controlledPurchases', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: { ...plain, weight: 'medium', allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: 'Controlled Purchases',
	headers: [{ id: 'h-controlled-purchases', text: 'Controlled Purchases' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// The header needs ~190px; a fixed column narrower than its header
	// overflows the table (the list budgets the configured width).
	width: 204,
};

/** Recently Fixed: what the file showed and what it shows now, then when and by whom. */
export const wasNowColumn: ListBuilderColumn = {
	id: 'compliance-was-now',
	fields: [
		{
			id: 'fix-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fixSummary', objectTable: TABLE },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusSuccess' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('fix-break-1'),
		{
			id: 'fixed-by',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'fixedBy', objectTable: TABLE },
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Was → Now',
	headers: [{ id: 'h-was-now', text: 'Was → Now' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	width: 236,
};

/**
 * Adverse Action: WHAT THE BOARD SAYS, and HOW FRESH that reading is.
 *
 * Two lines, both written by the API (licenses.service.ts):
 *   `boardActionSummary` — the action type, case number and dates where the
 *     regulator publishes them; otherwise the explicit sentence that it does
 *     not ("Flag only — <State> publishes no further detail"). Most flagged
 *     rows fleet-wide are flag-only, so the ceiling has to be on the row, not
 *     discovered by opening an empty panel.
 *   `boardReadAt` — the date we last read the regulator's own page. A board
 *     action is only as true as the last reading, and a stale one is a
 *     different decision from a fresh one.
 */
export const boardActionColumn: ListBuilderColumn = {
	id: 'compliance-board-action',
	fields: [
		{
			id: 'board-action-summary',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionSummary', objectTable: TABLE },
				defaultValue: 'Board action on record',
			},
			styleConfig: {
				size: 'sm',
				weight: 'semibold',
				colorRule: { type: 'theme', themeColor: 'statusWarning' },
				allowTextWrap: true,
			},
			displayStyle: 'text',
		},
		lineBreak('board-action-break-1'),
		{
			id: 'board-read-at',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardReadAt', objectTable: TABLE },
				defaultValue: 'Board page never read',
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'What the Board Says',
	headers: [{ id: 'h-board-action', text: 'What the Board Says' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// Sized like the other list-specific columns; the header itself needs
	// ~150px and the flag-only sentence wraps to two or three lines.
	width: 236,
};

/**
 * Adverse Action: the action's OWN dates, start stacked over end.
 *
 * Chris asked for these on the list on 2026-09-20 ("if the list knows the date
 * of the action start/end that would be great to add, and we can just stack
 * those and have dashes when unknown"), and the dashes are most of the column
 * by design: measured on prod the same day, 14 of 276 active-buyer rows yield a
 * start date and 4 yield an end date. A dash here is the BOARD's silence
 * reported, not a gap in our data, and the neighboring "What the Board Says"
 * column already states that ceiling in words.
 *
 * Both lines are written by the API (`boardActionDates` in libs/shared), which
 * is also what the review modal and the auto-sunset predicate read — one
 * reading of the board's dates, so the row, the modal and the queue can never
 * disagree. Each line leads with the BOARD's own frame for its date —
 * "Started" for a discipline start date, "Effective" for an effective date,
 * "Filed" where the board says it filed a charge it has not ruled on, and
 * "Ends"/"Ended" for the end date — so a filed accusation is never presented
 * as a decision the board has made.
 */
export const boardActionDatesColumn: ListBuilderColumn = {
	id: 'compliance-board-action-dates',
	fields: [
		{
			id: 'board-action-started',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionStarted', objectTable: TABLE },
				defaultValue: '\u2014',
			},
			styleConfig: plain,
			displayStyle: 'text',
		},
		lineBreak('board-action-dates-break-1'),
		{
			id: 'board-action-ended',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'boardActionEnded', objectTable: TABLE },
				defaultValue: '\u2014',
			},
			styleConfig: muted,
			displayStyle: 'text',
		},
	],
	label: 'Action Dates',
	headers: [{ id: 'h-board-action-dates', text: 'Action Dates' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	// The header needs ~95px and the longest line is "Effective 12/30/1992".
	width: 132,
};

/** One plain sentence, written by the API, saying why the row is on this list. */
export const whyHereColumn: ListBuilderColumn = {
	id: 'compliance-why-here',
	fields: [
		{
			id: 'why-here-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'whyHere', objectTable: TABLE },
			},
			styleConfig: { ...plain, allowTextWrap: true },
			displayStyle: 'text',
		},
	],
	label: "Why It's Here",
	headers: [{ id: 'h-why-here', text: "Why It's Here" }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: false,
	minWidth: 260,
};

/**
 * The one next step, as a labelled inline button. Anything that proposes a
 * license opens the matchmaker confirmation screen; nothing is applied from the
 * list (Chris 2026-09-18).
 */
export const actionColumn: ListBuilderColumn = {
	id: 'compliance-action',
	fields: [
		{
			id: 'compliance-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					// PD-16254: the matchmaker found an in-force license at this
					// address. Opens the confirmation screen ON that candidate; the
					// list itself never applies it.
					id: 'review-replacement-action',
					label: 'Review Replacement',
					iconName: 'clipboardCheck',
					callbackName: 'reviewReplacement',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					conditionalVisibility: {
						field: 'replacementMatchId',
						condition: 'is_not_null',
					},
				},
				{
					id: 'review-match-action',
					label: 'Review Match',
					iconName: 'clipboardCheck',
					callbackName: 'reviewMatch',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					// A pending match and NO replacement to review instead: two
					// fields, so the page supplies the predicate.
					visibilityCallback: 'canReviewMatch',
				},
				{
					id: 'search-registry-action',
					label: 'Search Registry',
					iconName: 'search',
					callbackName: 'searchForMatches',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
					// Only a license with NO link and NO pending suggestion: two
					// fields, so the page supplies the predicate.
					visibilityCallback: 'canSearchForMatches',
				},
				{
					// A LINKED license has nothing to review or search: the fix is on
					// the license itself (replace it, or unmatch it).
					id: 'open-license-action',
					label: 'Open License',
					iconName: 'arrowUpRightFromSquare',
					callbackName: 'openLicense',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					visibilityCallback: 'canOpenLicense',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// Sized to the longest label, "Review Replacement" (about 178px with its
	// icon) plus the cell padding. At 184 that button overflowed the cell to
	// the left, covering the reason text and clipping its rounded corners.
	width: 216,
};

/**
 * Adverse Action's single action: **Review**, which opens the review modal.
 *
 * Deliberately NOT `actionColumn`: none of that column's four actions applies
 * here. The license is linked and in force, so there is nothing to confirm,
 * search for or replace — the judgement is "should we keep selling on this?",
 * and that is not a decision a list row can carry. Seeing a row means a review
 * is needed; the modal is where the comparison happens (Chris, 2026-09-20).
 */
export const reviewActionColumn: ListBuilderColumn = {
	id: 'compliance-review-action',
	fields: [
		{
			id: 'compliance-review-actions',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'review-adverse-action',
					label: 'Review',
					iconName: 'clipboardCheck',
					callbackName: 'reviewAdverseAction',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:update',
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-compliance-review-action', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	// One short label, so this column is far narrower than the shared
	// `actionColumn` (which is sized to "Review Replacement"). 116px is the
	// button (~96px with its icon) plus the cell padding.
	width: 116,
};
